import { Dispatch, useCallback, useState } from 'react';
import { LinkTrackingData } from '../../../../../AnalyticsContext/LinkTracking';
import { TrackingEvent } from '../../../../../AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../../../../AnalyticsContext/useAnalytics';
import { useEnterKeyHandler } from '../../../../../hooks/keyboard/useEnterKeyHandler';
import { safeMemo } from '../../../../../util/safeMemo';
import { SuggestionData } from '../../types';
import { SingleViewSearchCategory } from '../SingleViewSearchCategory';
import { SuggestionLabel } from '../SuggestionLabel';
import { SeeAll } from './SuggestionList.styles';
import { SuggestionListItem } from './SuggestionListItem/SuggestionListItem';

interface Props {
  onChoose: (suggestion: SuggestionData, index: number) => void;
  searchTerm: string;
  suggestions: SuggestionData[];
  trackingData: LinkTrackingData;
  toggleSingleSuggestionView: Dispatch<SingleViewSearchCategory>;
  showSingleView?: boolean;
  suggestionType?: SuggestionLabel;
  indexIdentifier?: number;
}

export const SuggestionList = safeMemo(
  ({
    suggestions,
    onChoose,
    trackingData,
    searchTerm,
    toggleSingleSuggestionView,
    showSingleView,
    suggestionType,
    indexIdentifier,
  }: Props) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const { trackEvent } = useAnalytics();

    const chooseSelection = useCallback(() => {
      const localIndex = parseInt(`${selectedIndex}`.slice(-1), 10);

      const selectedSuggestion = suggestions[localIndex];

      if (selectedSuggestion) onChoose(selectedSuggestion, selectedIndex);
    }, [onChoose, selectedIndex, suggestions]);

    useEnterKeyHandler(() => {
      trackEvent(TrackingEvent.SEARCH_BAR_ENTER_PRESS, {
        searchData: {
          term: searchTerm, // Can move this higher (and stop passing searchTerm into this component)
        },
      });

      if (selectedIndex !== 0) {
        chooseSelection();
      }
    });

    const showSingleViewSuggestion = useCallback(
      () =>
        toggleSingleSuggestionView({
          showSingleView: true,
          category: suggestionType,
        }),
      [toggleSingleSuggestionView, suggestionType],
    );

    return (
      <>
        {suggestions.map((suggestion, i) => (
          <SuggestionListItem
            key={suggestion.id}
            index={(indexIdentifier || 0) * 10 + i}
            suggestion={suggestion}
            onChoose={onChoose}
            onMouseOver={setSelectedIndex}
            trackingData={trackingData}
          />
        ))}
        {!showSingleView && showSingleView !== undefined && (
          <SeeAll onClick={showSingleViewSuggestion}>See all</SeeAll>
        )}
      </>
    );
  },
);
